import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

/**
 * Auto-login page for SSO via iframe embedding.
 * 
 * URL format:
 * /auto-login?email={{user.email}}&name={{user.name}}&sso_key=SECRET&embed=true
 * 
 * Flow:
 * 1. Extract email, name, sso_key, embed from URL params
 * 2. Call /api/auth/sso to authenticate (auto-creates user if new)
 * 3. Store JWT token
 * 4. Redirect to dashboard (with embed=true if specified)
 */
export default function AutoLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Authenticating...');

  useEffect(() => {
    const email = searchParams.get('email');
    const name = searchParams.get('name') || '';
    const ssoKey = searchParams.get('sso_key');
    const embed = searchParams.get('embed');

    if (!email || !ssoKey) {
      setError('Missing required parameters (email, sso_key)');
      return;
    }

    const doLogin = async () => {
      try {
        setStatus('Authenticating...');
        const res = await api.post('/auth/sso', {
          email,
          name,
          sso_key: ssoKey,
        });

        const { token, user } = res.data;

        // Store auth data
        localStorage.setItem('hc_token', token);
        localStorage.setItem('hc_user', JSON.stringify(user));
        updateUser(user);

        setStatus('Redirecting...');

        // Build redirect URL
        const embedParam = embed === 'true' ? '?embed=true' : '';
        
        if (user.onboarding_complete) {
          navigate(`/dashboard${embedParam}`, { replace: true });
        } else {
          navigate(`/onboarding${embedParam}`, { replace: true });
        }
      } catch (err) {
        const detail = err.response?.data?.detail || 'Authentication failed';
        setError(detail);
      }
    };

    doLogin();
  }, [searchParams, navigate, updateUser]);

  return (
    <div className="min-h-screen bg-[--hc-bg] flex items-center justify-center">
      <div className="text-center">
        <img src="/logo.png" alt="HabitCloser" className="h-8 mx-auto mb-6" />
        {error ? (
          <div className="space-y-3">
            <p className="text-sm text-[--hc-danger]">{error}</p>
            <a
              href="/login"
              className="text-sm text-[--hc-primary] hover:underline"
            >
              Go to login page
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="w-6 h-6 border-2 border-[--hc-accent] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-[--hc-text-muted]">{status}</p>
          </div>
        )}
      </div>
    </div>
  );
}
